<template>
    <div class="chat-content" :class="{ 'modal': depth > 0 }" :style="{ zIndex: 1000 + depth }">
        <div class="chat-header" v-if="depth > 0">
            <h3>Trace Information (Level {{ depth }})</h3>
            <button class="close-button" @click="$emit('close')">&times;</button>
        </div>
        <div class="messages-container" ref="messagesContainer" @scroll="handleScroll">
            <ul class="chat-messages">
                <li v-for="message in messages" :key="message.id">
                    <div class="message"
                        :class="{ 'user-message': message.msgType === 'PlayerInput', 'bot-message': message.msgType !== 'PlayerInput' }"
                        @mouseover="startTooltipTimer(message, $event)"
                        @mouseleave="cancelTooltipTimer">
                        <div class="message-content" v-html="renderMarkdown(message.text)"></div>
                        
                        <!-- Message Buttons -->
                        <div class="message-buttons" v-if="message.buttons && message.buttons.length">
                            <button 
                                v-for="button in message.buttons" 
                                :key="button.id"
                                :disabled="button.disabled"
                                class="message-action-button" 
                                :class="{ 'disabled': button.disabled, 'highlight': button.highlight }"
                                :style="{
                                    backgroundColor: button.disabled ? '#cccccc' : (button.backgroundColor || '#007bff'),
                                    color: button.disabled ? '#666666' : (button.textColor || 'white'),
                                    borderColor: button.borderColor || 'transparent'
                                }"
                                @click="handleButtonClick(button, message)">
                                {{ button.text }}
                            </button>
                        </div>
                        
                        <div class="tags" v-if="message.tags && message.tags.length">
                            <span class="tag" v-for="tag in message.tags" :key="tag">{{ tag }}</span>
                        </div>
                        <div v-if="message.traceInfo && message.traceInfo.length" class="trace-info-icon"
                            @click.stop="$emit('openTraceInfo', message, depth)">
                            ℹ️
                        </div>
                    </div>
                </li>
            </ul>
            <button v-if="showTriggerSystem2Button" @click="$emit('triggerSystem2')" class="trigger-system-2-button">
                Trigger System 2
            </button>
            <button v-if="showScrollButton" @click="scrollToBottom" class="scroll-to-bottom">
                ⤓
            </button>
            <div v-if="isLoading" class="loading-message">
                <div class="loading-indicator"></div>
                AI is thinking...
            </div>
        </div>
        <div class="chat-input" v-if="depth === 0">
            <textarea 
                v-model="newMessage" 
                placeholder="Type your message..." 
                @keydown.enter.prevent="sendMessage"
                class="message-input"
            ></textarea>
            <button class="send-button" @click="sendMessage">Send</button>
            
            <!-- Single dynamic file upload button that replaces both previous buttons -->
            <label 
              v-if="fileUploadButton.visible"
              id="file_upload_button"
              class="attach-button"
              :class="{ 'highlight': fileUploadButton.highlight }"
              @click="showDocTypePicker"
            >
                {{ fileUploadButton.caption }}
            </label>
        </div>

        <!-- DocType Picker Modal -->
        <div v-if="showDocTypePickerModal" class="doc-type-picker-modal" @click.self="closeDocTypePicker">
            <div class="doc-type-picker-content">
                <h3>Select Document Type</h3>
                <div class="doc-type-list">
                    <button 
                        v-for="docType in availableDocTypes" 
                        :key="docType"
                        class="doc-type-button"
                        @click="selectDocType(docType)"
                    >
                        {{ docType }}
                    </button>
                </div>
                <button class="close-picker" @click="closeDocTypePicker">Cancel</button>
            </div>
        </div>

        <div v-if="tooltipVisible" class="tooltip" :style="tooltipStyle">
            <div v-html="tooltipContent"></div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, onMounted, nextTick, watch } from 'vue';
import { Message, MessageButton, FileAttachmentData } from './types';
import { FileUploadButton } from '../proto-gen-ref/audio_pb';
import MarkdownIt from 'markdown-it';
import { ProcessorService } from './ProcessorService';
import { useToast } from 'vue-toast-notification';
import { useFeatureFlagsStore } from './stores/featureFlags';
import { RealtimeAudioStreamingService } from './RealtimeApiAudioManager';

// Interface for file upload button state
interface FileUploadButtonState {
    visible: boolean;
    caption: string;
    highlight: boolean;
    handleFileSelectPurpose: string;
    defaultDocType: string;  // Renamed from handleFileSelectType
    customization?: FileUploadButton | null; // Store the entire customization object
}

export default defineComponent({
    props: {
        messages: {
            type: Array as PropType<Message[]>,
            required: true,
        },
        isLoading: {
            type: Boolean,
            required: true,
        },
        showTriggerSystem2Button: {
            type: Boolean,
            required: true,
        },
        depth: {
            type: Number,
            required: true,
        },
        auth: {
            type: Object,
            required: true,
        },
        chatService: {
            type: Object as PropType<ProcessorService>,
            required: true,
        },
    },
    // No additional data properties needed
    data() {
        return {};
    },
    emits: {
        openTraceInfo: (_message: Message, _depth: number) => true,
        triggerSystem2: () => true,
        close: () => true,
        sendMessage: (_text: string) => true,
        buttonClick: (_button: MessageButton, _message: Message) => true,
        attachFile: (_fileData: FileAttachmentData) => true
    },
    setup(props, { emit }) {
        const md = new MarkdownIt();

        // Configure markdown-it to add target="_blank" to all links
        const defaultRender = md.renderer.rules.link_open || function(tokens, idx, options, env, self) {
            return self.renderToken(tokens, idx, options);
        };
        md.renderer.rules.link_open = function(tokens, idx, options, env, self) {
            const token = tokens[idx];
            token.attrSet('target', '_blank');
            token.attrSet('rel', 'noopener noreferrer'); // Security best practice for target="_blank"
            return defaultRender(tokens, idx, options, env, self);
        };

        const newMessage = ref('');
        const messagesContainer = ref<HTMLElement | null>(null);
        const tooltipVisible = ref(false);
        const tooltipContent = ref('');
        const tooltipStyle = ref({
            top: '0px',
            left: '0px',
        });
        const tooltipTimer = ref<number | null>(null);
        const tooltipDelay = 500; // 500ms delay
        const autoScroll = ref(true);
        const showScrollButton = ref(false);
        const toast = useToast();
        const featureFlags = useFeatureFlagsStore();
        
        // Initialize the dynamic file upload button with default state
        const fileUploadButton = ref<FileUploadButtonState>({
            visible: false, // Hidden by default until configured
            caption: "Upload File",
            highlight: false,
            handleFileSelectPurpose: "vs_analyze",
            defaultDocType: "pdf",  // Renamed from handleFileSelectType
            customization: null // Initialize empty customization object
        });
        
        // DocType picker state
        const showDocTypePickerModal = ref(false);
        const availableDocTypes = ref<string[]>([]);

        // Show docType picker
        const showDocTypePicker = () => {
            // Get doc types at the time of click
            const docTypes = fileUploadButton.value.customization?.getAvailableDocTypesList() || [];
            
            if (docTypes.length === 1) {
                // If only one docType available, use it directly and skip picker
                console.log('[ChatContent] Single docType available, skipping picker:', docTypes[0]);
                selectDocType(docTypes[0]);
            } else if (docTypes.length > 1) {
                // Multiple docTypes, show picker
                availableDocTypes.value = docTypes;
                showDocTypePickerModal.value = true;
            } else {
                // If no docTypes available, proceed directly to file selection
                triggerFileInput();
            }
        };

        // Close docType picker
        const closeDocTypePicker = () => {
            showDocTypePickerModal.value = false;
        };

        // Select docType and proceed with file upload
        const selectDocType = (docType: string) => {
            closeDocTypePicker();
            triggerFileInput(docType);
        };

        // Trigger file input with optional docType
        const triggerFileInput = (docType?: string) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.pdf';
            input.onchange = (e) => handleFileSelect(e, docType || fileUploadButton.value.defaultDocType);
            input.click();
        };

        // Handle file selection
        const handleFileSelect = async (event: Event, docType: string) => {
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;

            const file = input.files[0];
            if (file.type !== 'application/pdf') {
                console.error('Only PDF files are allowed');
                // Show error to user
                toast.error('Please select a PDF file');
                return;
            }

            try {
                if (featureFlags.hasWebSocketFileUpload) {
                    // Use WebSocket-based chunked upload
                    const audioService = RealtimeAudioStreamingService.getInstance();
                    const transferId = await audioService.uploadFile(
                        `file_upload_${docType}`,
                        file,
                        docType
                    );

                    // Emit event with file details
                    emit('attachFile', {
                        purpose: fileUploadButton.value.handleFileSelectPurpose,
                        mimeType: file.type,
                        docType: docType,
                        name: file.name,
                        taskId: transferId,
                        size: file.size,
                        transport: 'websocket'
                    });
                } else {
                    // Use HTTP-based upload
                    console.log('Using HTTP-based file upload with docType:', docType);
                    const result = await props.chatService.streamUpload(file, docType, fileUploadButton.value.handleFileSelectPurpose);

                    // Emit event with file details
                    emit('attachFile', {
                        purpose: fileUploadButton.value.handleFileSelectPurpose,
                        mimeType: file.type,
                        docType: docType,
                        name: file.name,
                        taskId: result.task_id,
                        size: file.size,
                        transport: 'http'
                    });
                }

                // Reset file input
                input.value = '';
            } catch (error) {
                console.error('Error uploading file:', error);
                toast.error('Failed to upload file. Please try again.');
                return;
            }
        };

        const scrollToBottom = () => {
            nextTick(() => {
                if (messagesContainer.value) {
                    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
                    autoScroll.value = true;
                    showScrollButton.value = false;
                }
            });
        };

        const handleScroll = () => {
            if (messagesContainer.value) {
                const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value;
                const bottomThreshold = scrollHeight - clientHeight - 100; // 100px threshold
                
                autoScroll.value = scrollTop >= bottomThreshold;
                showScrollButton.value = !autoScroll.value;
            }
        };

        // Watch messages for changes to trigger auto-scroll
        watch(() => props.messages, () => {
            if (autoScroll.value) {
                scrollToBottom();
            } else if (!showScrollButton.value) {
                showScrollButton.value = true;
            }
        }, { deep: true });

        // Initialize button state based on feature flags
        onMounted(() => {
            scrollToBottom();
            
            console.log('[ChatContent] onMounted, featureFlags:', Array.from(featureFlags.flags).sort().join(', '));
            console.log('[ChatContent] hasJiraCloudIntegration:', featureFlags.hasJiraCloudIntegration);
            
            // Set initial button state based on feature flags
            if (featureFlags.hasJiraCloudIntegration) {
                console.log('[ChatContent] Setting button for Jira integration');
                fileUploadButton.value = {
                    visible: false, // Hide by default for Jira integration
                    caption: "Upload new contract to Jira",
                    highlight: false,
                    handleFileSelectPurpose: "jira",
                    defaultDocType: "contract",  // Renamed from handleFileSelectType
                    customization: null // Initialize empty customization object
                };
            } else {
                console.log('[ChatContent] Setting button for standard use');
                fileUploadButton.value = {
                    visible: true,
                    caption: "Attach pdf",
                    highlight: false,
                    handleFileSelectPurpose: "vs_analyze",
                    defaultDocType: "pdf",  // Renamed from handleFileSelectType
                    customization: null // Initialize empty customization object
                };
            }
            
            console.log('[ChatContent] Initial button state:', fileUploadButton.value);
        });

        // Handle button clicks
        const handleButtonClick = (button: MessageButton, message: Message) => {
            if (button.disabled) return;
            
            // Immediately disable all buttons in this message to prevent double-clicks
            // or starting multiple actions while one is in progress
            if (message.buttons) {
                message.buttons.forEach((btn: MessageButton) => {
                    btn.disabled = true;
                });
            }
            
            // Emit event to parent component
            emit('buttonClick', button, message);
        };
        
        // Method to update the button state (will be called from parent component)
        const updateFileUploadButton = (newState: Partial<FileUploadButtonState>) => {
            console.log('[ChatContent] updateFileUploadButton called with:', newState);
            console.log('[ChatContent] Current button state:', fileUploadButton.value);
            
            // Create a completely new object instead of merging to ensure reactivity
            const updatedState = {
                visible: newState.visible !== undefined ? newState.visible : fileUploadButton.value.visible,
                caption: newState.caption || fileUploadButton.value.caption,
                highlight: newState.highlight !== undefined ? newState.highlight : fileUploadButton.value.highlight,
                handleFileSelectPurpose: newState.handleFileSelectPurpose || fileUploadButton.value.handleFileSelectPurpose,
                defaultDocType: newState.defaultDocType || fileUploadButton.value.defaultDocType,  // Renamed from handleFileSelectType
                customization: newState.customization || fileUploadButton.value.customization
            };
            
            // Force full replacement for better reactivity
            fileUploadButton.value = updatedState;
            
            console.log('[ChatContent] New button state (fully replaced):', fileUploadButton.value);
        };

        return {
            md,
            newMessage,
            messagesContainer,
            tooltipVisible,
            tooltipContent,
            tooltipStyle,
            tooltipTimer,
            tooltipDelay,
            autoScroll,
            showScrollButton,
            scrollToBottom,
            handleScroll,
            toast,
            featureFlags,
            fileUploadButton,
            updateFileUploadButton,
            handleButtonClick,
            showDocTypePickerModal,
            availableDocTypes,
            showDocTypePicker,
            closeDocTypePicker,
            selectDocType,
            triggerFileInput,
            handleFileSelect,
            renderMarkdown: (text: string) => md.render(text),
            startTooltipTimer: (message: Message, event: MouseEvent) => {
                if (tooltipTimer.value) {
                    clearTimeout(tooltipTimer.value);
                }
                tooltipTimer.value = window.setTimeout(() => {
                    tooltipContent.value = message.text || '';
                    tooltipStyle.value = {
                        top: `${event.clientY + 10}px`,
                        left: `${event.clientX + 10}px`,
                    };
                    tooltipVisible.value = true;
                }, tooltipDelay);
            },
            cancelTooltipTimer: () => {
                if (tooltipTimer.value) {
                    clearTimeout(tooltipTimer.value);
                    tooltipTimer.value = null;
                }
                tooltipVisible.value = false;
            },
            sendMessage: () => {
                if (newMessage.value.trim()) {
                    emit('sendMessage', newMessage.value);
                    newMessage.value = '';
                }
            },
            handleKeydown: (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    emit('close');
                }
            }
        };
    },
    mounted() {
      window.addEventListener('keydown', this.handleKeydown);
    },
    beforeUnmount() {
      window.removeEventListener('keydown', this.handleKeydown);
    },
});
</script>

<style scoped>
.chat-content {
    display: flex;
    flex-direction: column;
    height: 100vh;
}

.chat-content.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.chat-content.modal .messages-container {
    background-color: white;
    border-radius: 5px;
    width: 80%;
    max-width: 800px;
    max-height: 80vh;
    overflow-y: auto;
}

.chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    background-color: #f0f0f0;
    border-bottom: 1px solid #ddd;
}

.close-button {
    font-size: 24px;
    background: none;
    border: none;
    cursor: pointer;
}

.messages-container {
    position: relative;
    height: 100%;
    overflow-y: auto;
    padding: 1rem;
}

.chat-messages {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
}

.message {
    position: relative;
    max-width: 80%;
    padding: 12px 16px;
    border-radius: 20px;
    margin-bottom: 10px;
    word-wrap: break-word;
    clear: both;
    width: max-content;
    min-width: 60px;
}

.user-message {
    align-self: flex-end;
    float: right;
    background-color: #007bff;
    color: white;
    border-radius: 20px 20px 20px 20px;
    margin-left: auto;
}

.bot-message {
    align-self: flex-start;
    float: left;
    background-color: transparent;
    color: #343A40;
    border-radius: 20px 20px 20px 20px;
}

.message-content {
    padding-right: 0;
    display: inline;
    width: auto;
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
}

.tag {
    font-size: 0.75rem;
    padding: 2px 6px;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0.1);
    color: inherit;
}

.message-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.message-action-button {
    padding: 6px 12px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
}

.message-action-button:hover:not([disabled]) {
    background-color: #0056b3;
}

.message-action-button.disabled,
.message-action-button[disabled] {
    background-color: #cccccc !important;
    color: #666666 !important;
    cursor: not-allowed;
    animation: none !important; /* Stop any animations */
    border: none !important; /* Remove any border */
}

.message-action-button.highlight:not([disabled]):not(.disabled) {
    background-color: #28a745;
    border: 2px solid #1e7e34;
    animation: highlight-pulse 2s infinite;
    font-weight: bold;
}

.trace-info-icon {
    position: absolute;
    top: 5px;
    right: 5px;
    cursor: pointer;
    font-size: 18px;
    background-color: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    /* Ensure icon is above message content */
}

.user-message .trace-info-icon {
    background-color: rgba(0, 0, 0, 0.1);
    color: white;
}

.chat-input {
    display: flex;
    padding-bottom: 42px;
    border-top: 1px solid #ddd;
    background-color: #ffffff;
    align-items: flex-end;
}

.message-input {
    flex-grow: 1;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    resize: none;
    min-height: 60px;
    max-height: 120px;
    overflow-y: auto;
    font-family: inherit;
    font-size: inherit;
    line-height: 1.4;
}

.chat-input button {
    margin-left: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.chat-input button:hover {
    background-color: #0056b3;
}

.loading-message {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    opacity: 0.7;
}

.loading-indicator {
    width: 20px;
    height: 20px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 10px;
}

.trigger-system-2-button {
    margin-top: 10px;
    padding: 8px 16px;
    background-color: #28A745;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.tooltip {
    position: fixed;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 10px;
    border-radius: 5px;
    z-index: 1000;
    max-width: 300px;
    overflow: auto;
    max-height: 200px;
}

.tooltip pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
}

.scroll-to-bottom {
    position: fixed;
    bottom: 100px;
    right: 20px;
    background-color: var(--vscode-badge-background);
    color: var(--vscode-badge-foreground);
    border: none;
    border-radius: 4px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    z-index: 1000;
    opacity: 0.8;
}

.scroll-to-bottom:hover {
    opacity: 1;
    background-color: var(--vscode-badge-background);
}

.attach-button {
    padding: 0.5rem 1rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-left: 0.5rem;
}

.attach-button:hover {
    background-color: #0056b3;
}

/* Highlighted attach button */
.attach-button.highlight {
    background-color: #28a745;
    border: 2px solid #1e7e34;
    animation: highlight-pulse 2s infinite;
    font-weight: bold;
}

@keyframes highlight-pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
    }
    70% {
        box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
    }
}

.doc-type-picker-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.doc-type-picker-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    min-width: 300px;
    max-width: 500px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.doc-type-picker-content h3 {
    margin-top: 0;
    margin-bottom: 16px;
    color: #333;
}

.doc-type-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
}

.doc-type-button {
    padding: 10px 16px;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s;
}

.doc-type-button:hover {
    background-color: #e9e9e9;
}

.close-picker {
    width: 100%;
    padding: 8px;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
}

.close-picker:hover {
    background-color: #e9e9e9;
}
</style>
